
import { defineComponent, ref, computed, watch, PropType } from 'vue'
import FactorGroup from './FactorGroup.vue'
import FactorDescDialog from './FactorDescDialog.vue'

// 从原始树中提取所有叶子因子（attrType !== null），附带 rootName
function extractFactors(nodes: any[], rootName = ''): any[] {
  const result: any[] = []
  for (const node of nodes) {
    const name = rootName || node.attrName
    if (node.attrType !== null && node.attrType !== undefined) {
      result.push({ ...node, rootName: name })
    }
    if (node.childAttrList?.length) {
      result.push(...extractFactors(node.childAttrList, name))
    }
  }
  return result
}

export default defineComponent({
  name: 'FactorSelector',
  components: { FactorGroup, FactorDescDialog },
  props: {
    factorData: { type: Array as PropType<any[]>, default: () => [] },
    isLight: Boolean,
    colors: { type: Object as PropType<Record<string, string>>, required: true },
    headers: { type: Object as PropType<Record<string, string>>, default: () => ({}) }
  },
  emits: ['change'],
  setup(props, { emit }) {
    const activeTab = ref('')
    const selectedFactors = ref<any[]>([])
    const searchKeyword = ref('')
    const showDropdown = ref(false)
    const highlightCode = ref('')

    // 一级 tab 树
    const factorTree = computed(() => props.factorData || [])

    // 初始化默认选中第一个 tab
    watch(factorTree, (val) => {
      if (val.length && !activeTab.value) {
        activeTab.value = val[0].attrCode
      }
    }, { immediate: true })

    const currentTab = computed(() =>
      factorTree.value.find((t: any) => t.attrCode === activeTab.value)
    )

    // 已选 code 集合，方便快速判断
    const selectedCodes = computed(() => new Set(selectedFactors.value.map((f: any) => f.attrCode)))

    // 已选因子 Map，用于回显 selectedOptions
    const selectedMap = computed(() => new Map(selectedFactors.value.map((f: any) => [f.attrCode, f])))

    // 所有叶子因子（用于搜索）
    const allFactors = computed(() => extractFactors(factorTree.value))

    // 搜索结果
    const searchResults = computed(() => {
      if (!searchKeyword.value.trim()) return []
      const kw = searchKeyword.value.trim().toLowerCase()
      return allFactors.value.filter((f: any) => f.attrName?.toLowerCase().includes(kw)).slice(0, 20)
    })

    const showDescDialog = ref(false)
    const descDialogTab = ref<any>(null)

    const openDescDialog = (tab: any) => {
      descDialogTab.value = tab
      showDescDialog.value = true
    }

    // 判断 tab 是否有可展示的说明内容
    const tabHasDesc = (tab: any): boolean => {
      const check = (nodes: any[]): boolean => nodes.some((n: any) => {
        if (n.attrType !== null && n.attrType !== undefined) {
          // 有子选项且子选项中有说明才算有描述
          const childrenHaveDesc = (n.childAttrList || []).some((c: any) => c.attrDescShowStatus && c.attrDesc)
          return childrenHaveDesc || !!(n.attrDescShowStatus && n.attrDesc)
        }
        return check(n.childAttrList || [])
      })
      return check(tab.childAttrList || [])
    }

    const onSearch = () => { showDropdown.value = true }
    const onBlur = () => { setTimeout(() => { showDropdown.value = false }, 150) }

    // 获取 tab 的因子说明（子因子 attrDesc 合集）
    const getTabDesc = (tab: any): string => {
      const descs = (tab.childAttrList || [])
        .filter((c: any) => c.attrType !== null && c.attrType !== undefined && c.attrDesc)
        .map((c: any) => c.attrDesc as string)
      return descs.join('；')
    }

    const selectFromSearch = (item: any) => {
      showDropdown.value = false
      searchKeyword.value = ''
      // 切换到对应的一级 tab
      const root = factorTree.value.find((t: any) => t.attrName === item.rootName)
      if (root) activeTab.value = root.attrCode
      // 高亮定位
      highlightCode.value = item.attrCode
      setTimeout(() => { highlightCode.value = '' }, 2000)
    }

    const onToggleFactor = (factor: any) => {
      const idx = selectedFactors.value.findIndex((f: any) => f.attrCode === factor.attrCode)
      if (idx >= 0) {
        // attrType 7 或已确认的因子：始终更新不删除
        if (factor.attrType === 7 || factor._confirmed || factor.selectedOptions?.length || factor.selectedRange) {
          selectedFactors.value.splice(idx, 1, factor)
        } else {
          selectedFactors.value.splice(idx, 1)
        }
      } else {
        selectedFactors.value.push(factor)
      }
      emit('change', selectedFactors.value)
    }

    const removeSelected = (item: any) => {
      selectedFactors.value = selectedFactors.value.filter((f: any) => f.attrCode !== item.attrCode)
      emit('change', selectedFactors.value)
    }

    // 已选因子标签展示文案，按 attrType 格式化
    const formatSelectedLabel = (item: any): string => {
      const name = item.attrName
      const opts: any[] = item.selectedOptions || []
      const sep = item.unionMode === 'intersect' ? ' 且 ' : ' 或 '
      const range = item.selectedRange
      switch (item.attrType) {
        case 1: return opts.length ? `${name}（${opts[0].attrName}）` : name
        case 2: return opts.length ? `${name}：${opts[0].attrName}` : name
        case 3: return opts.length ? `${name}：${opts.map((o: any) => o.attrName).join(' 或 ')}` : name
        case 4: return opts.length ? `${name}：${opts.map((o: any) => o.attrName).join(sep)}` : name
        case 5: return opts.length ? `${name}：${opts.map((o: any) => o.attrName).join(' 或 ')}` : name
        case 6: return opts.length ? `${name}：${opts.map((o: any) => o.attrName).join(sep)}` : name
        case 7:
          if (range?.left && range?.right) return `${range.left}<=${name}<=${range.right}`
          if (range?.left) return `${name}>=${range.left}`
          if (range?.right) return `${name}<=${range.right}`
          return name
        default: return name
      }
    }

    return {
      activeTab, factorTree, currentTab,
      selectedFactors, selectedCodes, selectedMap,
      searchKeyword, searchResults, showDropdown,
      highlightCode,
      onSearch, onBlur, selectFromSearch,
      onToggleFactor, removeSelected, formatSelectedLabel,
      getTabDesc, tabHasDesc,
      showDescDialog, descDialogTab, openDescDialog
    }
  }
})
